import {Button, Card, Divider, Dropdown, Form, Input, Radio, Select, Space, Switch, Typography} from "antd";
import DatePicker from "../../../core/components/DatePicker";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import ModalitySelectBox from "../../modality/ModalitySelectBox";
import HospitalTreeView from "../../hospital/HospitalTreeView";
import { DatePickerFooter } from "../../../core/components/DatePickerFooter";
import {
    DATE_OPTIONS,
    getBeginLocalStr,
    getBeginStr,
    getDateFromNow,
    getEndLocalStr,
    getEndStr
} from "../../../core/utils/date-utils";
import QuickDatePicker from "../../../core/components/data-grid/QuickDatePicker";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import HospitalGroupSelectBox from "../../hospital-group/HospitalGroupSelectBox";
import UserSelectBox from "../../user/UserSelectBox";
import HospitalSelectBox from "../../hospital/HospitalSelectBox";
import { LocalStorageService } from "../../../core/service/local-storage-service";
import InstitutionSelect from "../../user/InstitutionSelect";
import { parseListInput } from "../../../core/utils/parse-list-input";
import "../../../core/style/grid-filter.css";
import { t } from 'i18next';

const { RangePicker } = DatePicker;

const getDefaultReportDate = () => {
    const date = getDateFromNow(DATE_OPTIONS.LAST_15_DAYS);
    return [date.start, date.end]
}

const getDefaultFilter = () => {
    const date = getDefaultReportDate();

    return window.reportFilter || {
        reportDateBegin: date[0],
        reportDateEnd: date[1],
        isDeleted: false
    }
}

// A field is "filled" (highlight it) if it has any value — even a default.
const isFilled = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
};

// Fields inside the collapsed "Detail Filters" section → auto-expand if active.
const DETAIL_FIELDS = [
    'institutions', 'isConfirm', 'isSign', 'isHbysTransfer', 'readUserId', 'writeUserId',
    'studyTypeList', 'studyStatus', 'assignDoctor', 'assignReporter', 'isEmergency', 'isDeleted',
];

const isValueActive = (key, v, defaults) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.filter(x => x !== null && x !== undefined && x !== -1).length > 0;
    if (v === -1) return false;
    if (defaults && Object.prototype.hasOwnProperty.call(defaults, key) && defaults[key] === v) return false;
    return true;
};

const hasActiveDetailFilter = (vals, defaults) =>
    DETAIL_FIELDS.some(k => isValueActive(k, vals?.[k], defaults));

export default function ReportFilter() {
    const defaultFilter = useRef(getDefaultFilter());
    const [dateType, setDateType] = useState('report')
    const [showDetail, setShowDetail] = useState(false)
    const [searchText, setSearchText] = useState(defaultFilter.current.searchText || '');
    const [dates, setDates] = useState({
        dateTimeBegin: defaultFilter.current.reportDateBegin,
        dateTimeEnd: defaultFilter.current.reportDateEnd,
    })
    const { hasPermission, user } = useUser();

    const { t } = useTranslation();
    const formRef = useRef();

    const { onFilterPageSort } = useDataGrid();

    // Track form values to highlight every filled filter input (amber = dolu filtre).
    const [formValues, setFormValues] = useState({});
    // searchText has its own (box) highlight; keep its input white.
    const activeFields = Object.keys(formValues).filter(k => isFilled(formValues[k]) && k !== 'searchText');
    const highlightCss = activeFields.map(f => {
        const base = `.grid-filter-form .ant-form-item:has([id="${f}"])`;
        // Never highlight a disabled control.
        return `${base} .ant-form-item-label>label{color:var(--accent-dark);font-weight:600}`
            + `${base} .ant-input:not([disabled]),`
            + `${base} .ant-select:not(.ant-select-disabled) .ant-select-selector,`
            + `${base} .ant-picker:not(.ant-picker-disabled),`
            + `${base} .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled){border-color:var(--accent) !important;background:var(--accent-soft) !important}`;
    }).join('');

    const searchActive = !!(searchText && searchText.length > 0);

    // Highlight each date box on its OWN value; skip while disabled (search active).
    const dateStyle = (v) => (v && !searchActive)
        ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
        : undefined;

    // Paste sırasında baş/son boşlukları kırp.
    const handleTrimPaste = (e) => {
        const text = (e.clipboardData || window.clipboardData)?.getData('text');
        if (text == null) return;
        const trimmed = text.trim();
        if (trimmed === text) return;
        e.preventDefault();
        document.execCommand('insertText', false, trimmed);
    };

    const filter = async (form, dates) => {
        let filter = {
            ...form,
        };

        // Çoklu Study ID / Study UID (virgül, boşluk veya satır ile ayrık) -> liste
        filter.studyIdList = parseListInput(form.studyIdText).map(x => parseInt(x, 10)).filter(x => !isNaN(x));
        filter.studyInstanceUidList = parseListInput(form.studyInstanceUidText);
        delete filter.studyIdText;
        delete filter.studyInstanceUidText;

        if (dateType === 'report') {
            filter = {
                ...filter,
                reportDateBegin: dates.dateTimeBegin,
                reportDateEnd: dates.dateTimeEnd,
            }
        }
        else if (dateType === 'study') {
            filter = {
                ...filter,
                studyDateBegin: dates.dateTimeBegin,
                studyDateEnd: dates.dateTimeEnd,
            }
        }
        else if (dateType === 'sign') {
            filter = {
                ...filter,
                signDateBegin: dates.dateTimeBegin,
                signDateEnd: dates.dateTimeEnd,
            }
        }

        window.reportFilter = filter;

        onFilterPageSort({
            filter
        })
    }

    const reset = async () => {
        window.location.reload()
    }

    useEffect(() => {
        // Formu uygulanan filtreyle (window.reportFilter) açıkça senkronla.
        // initialValues sabit varsayılanlar; bu olmadan grid filtreli yüklenip
        // form alanları boş/varsayılan kalıyordu (özellikle ileri/geri durumlarda).
        const saved = window.reportFilter;

        // A detail filter is active but hidden → open the section so the user sees it.
        if (hasActiveDetailFilter(saved || defaultFilter.current, defaultFilter.current)) {
            setShowDetail(true);
        }

        if (saved && formRef.current) {
            formRef.current.setFieldsValue({
                ...saved,
                studyIdText: (saved.studyIdList || []).join(', '),
                studyInstanceUidText: (saved.studyInstanceUidList || []).join(', '),
            });

            setSearchText(saved.searchText || '');

            if (saved.signDateBegin || saved.signDateEnd) {
                setDateType('sign');
                setDates({
                    dateTimeBegin: saved.signDateBegin,
                    dateTimeEnd: saved.signDateEnd,
                });
            } else if (saved.studyDateBegin || saved.studyDateEnd) {
                setDateType('study');
                setDates({
                    dateTimeBegin: saved.studyDateBegin,
                    dateTimeEnd: saved.studyDateEnd,
                });
            } else {
                setDateType('report');
                setDates({
                    dateTimeBegin: saved.reportDateBegin,
                    dateTimeEnd: saved.reportDateEnd,
                });
            }
        }

        // Seed tracked values (incl. defaults) so filled inputs highlight on load.
        if (formRef.current) {
            setFormValues(formRef.current.getFieldsValue(true));
        }

        onFilterPageSort({
            filter: defaultFilter.current
        })
    }, [])

    return (
        <>
            {highlightCss && <style>{highlightCss}</style>}
            <Form
                ref={formRef}
                className={'grid-filter-form'}
                layout={'vertical'}
                onFinish={(form) => filter(form, dates)}
                onValuesChange={(_, all) => setFormValues(all)}
                initialValues={{
                    reportDateBegin: getDefaultReportDate()[0],
                    reportDateEnd: getDefaultReportDate()[1],
                    isRead: null,
                    isReportWritten: null,
                    hasVoiceRecord: null,
                    isDeleted: false,
                    studyTypeList: [],
                    studyStatus: [],
                    hospitalIdList: []
                }}
            >
                <div style={{
                    background: searchActive ? 'var(--accent-soft)' : 'var(--surface-2)',
                    padding: '8px',
                    borderRadius: 6,
                    marginBottom: 8,
                    border: searchActive ? '1px solid var(--accent)' : '1px solid var(--border)'
                }}>
                    <div style={{
                        marginBottom: 4,
                        fontSize: '11px',
                        color: searchActive ? 'var(--accent-dark)' : 'var(--text-2)',
                        fontStyle: 'italic'
                    }}>
                        {t('Search by patient name, ID, accession number or description - searches all dates')}
                    </div>
                    <Form.Item
                        name='searchText'
                        style={{ marginBottom: 0 }}
                    >
                        <Input.Search
                            placeholder={t('Type anything: name, ID, number...')}
                            allowClear
                            enterButton={t('Search')}
                            size="small"
                            onPaste={handleTrimPaste}
                            onSearch={() => {
                                formRef.current.submit();
                            }}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Form.Item>
                </div>

                <Space style={{ marginBottom: 16 }}>
                    <Button type={'primary'} size={'small'} htmlType="submit" >{t('Search')}</Button>
                    <Button size={'small'} onClick={reset}>{t('Reset')}</Button>
                </Space>

                <Form.Item
                    label={
                        (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Radio.Group
                                    disabled={searchText && searchText.length > 0}
                                    options={[{ label: t('Report'), value: 'report' }, { label: t('Study'), value: 'study' }, { label: t('Signature'), value: 'sign' },]}
                                    onChange={(e) => setDateType(e.target.value)}
                                    value={dateType}
                                />
                            </div>
                        )
                    }
                    style={{ margin: 0, padding: 0 }}
                >
                    <div
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <DatePicker
                            disabled={searchText && searchText.length > 0}
                            locale={LocalStorageService.getAntLocale()}
                            value={dates.dateTimeBegin}
                            showToday={false}
                            style={dateStyle(dates.dateTimeBegin)}
                            onChange={(date) => {
                                setDates({
                                    ...dates,
                                    dateTimeBegin: date
                                })
                            }}
                        />
                        <DatePicker
                            disabled={searchText && searchText.length > 0}
                            locale={LocalStorageService.getAntLocale()}
                            value={dates.dateTimeEnd}
                            showToday={false}
                            style={dateStyle(dates.dateTimeEnd)}
                            onChange={(date) => {
                                setDates({
                                    ...dates,
                                    dateTimeEnd: date
                                })
                            }}
                        />
                    </div>
                </Form.Item>

                <Form.Item>
                    <QuickDatePicker
                        disabled={searchText && searchText.length > 0}
                        onChange={(start, end) => {
                            setDates({
                                dateTimeBegin: start,
                                dateTimeEnd: end
                            })
                        }} />
                </Form.Item>

                {/*<Form.Item*/}
                {/*    label={t('Study Date')}*/}
                {/*    style={{margin: 0, padding: 0}}*/}
                {/*>*/}
                {/*    <div*/}
                {/*        style={{display: 'flex', alignItems: 'center'}}*/}
                {/*    >*/}
                {/*        <DatePicker*/}
                {/*            locale={LocalStorageService.getAntLocale()}*/}
                {/*            value={dates.studyDateBegin}*/}
                {/*            showToday={false}*/}
                {/*            onChange={(date) => {*/}
                {/*                setDates({*/}
                {/*                    ...dates,*/}
                {/*                    reportDateBegin: null,*/}
                {/*                    reportDateEnd: null,*/}
                {/*                    studyDateBegin: date,*/}
                {/*                })*/}
                {/*            }}*/}
                {/*        />*/}
                {/*        <DatePicker*/}
                {/*            locale={LocalStorageService.getAntLocale()}*/}
                {/*            value={dates.studyDateEnd}*/}
                {/*            showToday={false}*/}
                {/*            onChange={(date) => {*/}
                {/*                setDates({*/}
                {/*                    studyDateBegin: dates.studyDateBegin ?? date,*/}
                {/*                    studyDateEnd: date,*/}
                {/*                    reportDateBegin: null,*/}
                {/*                    reportDateEnd: null,*/}
                {/*                })*/}
                {/*            }}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</Form.Item>*/}

                {/*<Form.Item>*/}
                {/*    <QuickDatePicker onChange={(start, end) => {*/}
                {/*        setDates({*/}
                {/*            studyDateBegin: start,*/}
                {/*            studyDateEnd: end,*/}
                {/*            reportDateBegin: null,*/}
                {/*            reportDateEnd: null*/}
                {/*        })*/}
                {/*    }} />*/}
                {/*</Form.Item>*/}

                {
                    hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && (
                        <Form.Item
                            label={t('Hospital Groups')}
                            name="hospitalGroupIdList"
                        >
                            <HospitalGroupSelectBox />
                        </Form.Item>
                    )
                }

                <Form.Item
                    label={t('Patient Id')}
                    name="patientId"
                >
                    <Input onPaste={handleTrimPaste} />
                </Form.Item>

                <Form.Item
                    label={t('Patient Name')}
                    name="patientName"
                >
                    <Input onPaste={handleTrimPaste} />
                </Form.Item>

                <Form.Item
                    label={t('Accession No')}
                    name="accessionNumber"
                >
                    <Input onPaste={handleTrimPaste} />
                </Form.Item>

                <Form.Item
                    label={t('Study ID')}
                    name="studyIdText"
                >
                    <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        onPaste={handleTrimPaste}
                        placeholder={t('Separate values with comma, space or new line')}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Study UID')}
                    name="studyInstanceUidText"
                >
                    <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        onPaste={handleTrimPaste}
                        placeholder={t('Separate values with comma, space or new line')}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Hospital Name')}
                    name="hospitalIdList"
                >
                    <HospitalSelectBox
                    />
                </Form.Item>

                <Form.Item
                    label={t('Modality')}
                    name="modalities"
                >
                    <ModalitySelectBox />
                </Form.Item>

                <Divider>
                    <div>
                        <Typography.Text style={{ fontWeight: 'bold', marginRight: 8 }}>{t('Detail Filters')} </Typography.Text>
                        <Switch
                            checked={showDetail}
                            onChange={(e) => setShowDetail(e)}
                        />
                    </div>
                </Divider>

                <div
                    style={{
                        marginTop: 16,
                        display: showDetail ? '' : 'none'
                    }}
                >
                    {
                        hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION) && (
                            <InstitutionSelect
                                hide={true}
                                formItem={{
                                    label: t('Institution'),
                                    name: 'institutions'
                                }}
                            />
                        )
                    }

                    <Form.Item
                        label={t('Confirming Status')}
                        name={'isConfirm'}
                    >
                        <Select>
                            <Select.Option value={null}>{t('All')}</Select.Option>
                            <Select.Option value={true}>{t('Confirmed')}</Select.Option>
                            <Select.Option value={false}>{t('Not Confirmed')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('Signature Status')}
                        name={'isSign'}
                    >
                        <Select>
                            <Select.Option value={null}>{t('All')}</Select.Option>
                            <Select.Option value={true}>{t('Signed')}</Select.Option>
                            <Select.Option value={false}>{t('Not Signed')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('Hbys Transfer Status')}
                        name={'isHbysTransfer'}
                    >
                        <Select>
                            <Select.Option value={null}>{t('All')}</Select.Option>
                            <Select.Option value={true}>{t('Transferred')}</Select.Option>
                            <Select.Option value={false}>{t('Not Transferred')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('Read User')}
                        name="readUserId"
                    >
                        <UserSelectBox type={'read'} />
                    </Form.Item>

                    <Form.Item
                        label={t('Wrote User')}
                        name="writeUserId"
                    >
                        <UserSelectBox type={'write'} />
                    </Form.Item>

                    <Form.Item
                        label={t('Study Type')}
                        name={'studyTypeList'}
                    >
                        <Select
                            mode={'multiple'}
                        >
                            <Select.Option value={null}>{t('All')}</Select.Option>
                            <Select.Option value={1}>{t('Routine')}</Select.Option>
                            <Select.Option value={2}>{t('Inpatient')}</Select.Option>
                            <Select.Option value={3}>{t('Emergency')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('Study Status')}
                        name={'studyStatus'}
                    >
                        <Select>
                            <Select.Option value={-1}>{t('All')}</Select.Option>
                            <Select.Option value={0}>{t('Not Completed')}</Select.Option>
                            <Select.Option value={3}>{t('Reports not confirmed')}</Select.Option>
                            <Select.Option value={1}>{t('Timeout, Reports not confirmed')}</Select.Option>
                            <Select.Option value={2}>{t('Completed')}</Select.Option>
                        </Select>
                    </Form.Item>

                    {
                        hasPermission(PERMISSIONS.STUDY_SET_READ) && (
                            <Form.Item
                                label={t('Assigned (Doctor)')}
                                name={'assignDoctor'}
                            >
                                <Select>
                                    <Select.Option value={null}>{t('All')}</Select.Option>
                                    <Select.Option value={user?.data?.id}>{t('Assigned Me')}</Select.Option>
                                </Select>
                            </Form.Item>
                        )
                    }

                    {
                        hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                            <Form.Item
                                label={t('Assigned (Reporter)')}
                                name={'assignReporter'}
                            >
                                <Select>
                                    <Select.Option value={null}>{t('All')}</Select.Option>
                                    <Select.Option value={user?.data?.id}>{t('Assigned Me')}</Select.Option>
                                </Select>
                            </Form.Item>
                        )
                    }

                    <Form.Item
                        label={t('Is Emergency')}
                        name={'isEmergency'}
                    >
                        <Select>
                            <Select.Option value={null}>{t('All')}</Select.Option>
                            <Select.Option value={true}>{t('Emergency')}</Select.Option>
                            <Select.Option value={false}>{t('Not Emergency')}</Select.Option>
                        </Select>
                    </Form.Item>

                    {
                        hasPermission(PERMISSIONS.STUDY_REQUEST_LIST_DELETED) && (
                            <Form.Item
                                label={t('Is Deleted')}
                                name={'isDeleted'}
                            >
                                <Select>
                                    <Select.Option value={null}>{t('All')}</Select.Option>
                                    <Select.Option value={true}>{t('Deleted')}</Select.Option>
                                    <Select.Option value={false}>{t('Not Deleted')}</Select.Option>
                                </Select>
                            </Form.Item>
                        )
                    }
                </div>
            </Form>
        </>
    )
}
